import {
  AuthenticatedUser,
  AuthenticatedUserJobType,
} from '@airship-platform/airship-patterns';
import { Appointment } from '../../../data-access/appointments';

type AppointmentPartial = Pick<Appointment, 'appointment'>;

const statusMap: Record<
  AppointmentPartial['appointment']['status'],
  AuthenticatedUserJobType | undefined
> = {
  SCHEDULED: 'Scheduled',
  'IN-PROGRESS': 'Working',
  DISPATCHED: 'Dispatched',
  COMPLETED: undefined,
  'ON-HOLD': undefined,
  CANCELLED: undefined,
};

interface GroupedAppointment<T extends AppointmentPartial> {
  date: string;
  appointments: T[];
}

export const filterAppointments = <T extends AppointmentPartial>(
  appointments: T[],
  config: AuthenticatedUser['config'],
  now = Date.now()
) => {
  const jobsFilter = config?.jobsFilter;
  const { jobTypes, returnCount, startsBeforeOffset, startsOnOrAfterOffset } = {
    jobTypes: jobsFilter?.jobTypes || ['Scheduled', 'Dispatched', 'Working'],
    returnCount: jobsFilter?.returnCount || 5000,
    startsBeforeOffset: (jobsFilter?.endOffset || 2880) * 60 * 1000, // default 2 days
    startsOnOrAfterOffset: 1440 * 60 * 1000, // default 1 day
  };

  return appointments
    .filter((appt) => {
      const jobType = statusMap[appt.appointment.status];
      const startDate = new Date(appt.appointment.startDateTime).getTime();
      if (startDate > now + startsBeforeOffset) {
        return false;
      }
      if (startDate < now - startsOnOrAfterOffset) {
        return false;
      }
      if (!jobType || !jobTypes.includes(jobType)) {
        return false;
      }

      return true;
    })
    .sort(
      (a, b) =>
        a.appointment.startDateTime.localeCompare(
          b.appointment.startDateTime
        ) || a.appointment.endDateTime.localeCompare(b.appointment.endDateTime)
    )
    .slice(0, returnCount);
};

export const groupAppointmentsByDay = <T extends AppointmentPartial>(
  appointments: T[]
): GroupedAppointment<T>[] => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const grouped = appointments.reduce<Record<string, T[]>>((prev, curr) => {
    const date = new Date(curr.appointment.startDateTime);
    const formattedDate = formatter.format(date);

    return Object.assign(prev, {
      [formattedDate]: [...(prev[formattedDate] || []), curr],
    });
  }, {});

  return Object.entries(grouped).map(([date, appointments]) => ({
    date,
    appointments,
  }));
};
